import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { HoldGate, STING_END_MS, STING_HOLD_END_MS } from '@aeonia/gaia-client';
import { playCanvas2dSting } from './sting/canvas2dFallback';
import { createWebglSting } from './sting/webglSting';
import type { WebglSting } from './sting/webglSting';

// Under the page's base path: the build may be served from /client/, not the site root.
const MARK_URL = `${import.meta.env.BASE_URL}mark-solid-480x240-clean.png`;

export type StingHandle = {
  /** Start the sting. Calling it again while one is running does nothing. */
  play(): void;
  /** Park at the hold until `promise` settles, then run the ring and the fade. */
  holdUntil(promise: Promise<unknown>): void;
  /** Release the hold by hand, for when there is nothing to wait on. */
  release(): void;
};

type StingProps = {
  /** Called once the fade has finished and the canvas is black again. */
  onDone?: () => void;
  /** Force the plain-drawing path even where WebGL2 works, for testing. */
  force2d?: boolean;
  /** Dev-only test hook (`?stingBreakShader=1`): make the shader fail to compile. */
  breakShader?: boolean;
  ref?: React.Ref<StingHandle>;
};

function loadMark(): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('the mark image failed to load'));
    image.src = MARK_URL;
  });
}

/** Ask for a WebGL2 context, treating any failure as "not available". */
function tryWebgl2(canvas: HTMLCanvasElement): WebGL2RenderingContext | null {
  try {
    return canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
    });
  } catch {
    return null;
  }
}

/**
 * The o¿o mark sting: one 600 by 600 canvas laid over the whole page.
 *
 * The canvas is black and the mark is drawn as added light, so nothing but the
 * mark's own glow shows. Where the browser gives a WebGL2 context the measured
 * shader runs in its wave mode; otherwise the plain-drawing fallback runs.
 *
 * The animation parks at the end of the hold beat, drawing nothing and holding
 * no animation frame, until the hold is released.
 */
export default function Sting({ onDone, force2d, breakShader, ref }: StingProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gateRef = useRef<HoldGate>(new HoldGate());
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Set once, the first (and only) time the WebGL2 shader fails to compile.
  // A canvas that has already given out a WebGL2 context can never return a
  // 2D one, so the fallback needs a fresh canvas element. Changing this flips
  // the canvas's `key`, which makes React tear down the old canvas and mount
  // a brand new one for the fallback to draw on.
  const [fallbackForced, setFallbackForced] = useState(false);
  const effectiveForce2d = force2d || fallbackForced;

  // Everything the imperative handle needs, kept out of React state so that
  // starting the sting never re-renders the page mid-animation.
  const runtimeRef = useRef({
    markImage: null as HTMLImageElement | null,
    webgl: null as WebglSting | null,
    playing: false,
    pending: false,
    handle: 0,
    startedAt: 0,
    cancel2d: null as (() => void) | null,
    disposed: false,
  });

  useImperativeHandle(
    ref,
    () => ({
      play(): void {
        startSting();
      },
      holdUntil(promise: Promise<unknown>): void {
        const gate = gateRef.current;
        promise.then(
          () => gate.release(),
          () => gate.release(),
        );
      },
      release(): void {
        gateRef.current.release();
      },
    }),
    [],
  );

  function finish(): void {
    const runtime = runtimeRef.current;
    if (runtime.handle) cancelAnimationFrame(runtime.handle);
    runtime.handle = 0;
    runtime.playing = false;
    runtime.webgl?.clear();
    onDoneRef.current?.();
  }

  function shaderFrame(now: number): void {
    const runtime = runtimeRef.current;
    if (runtime.disposed || !runtime.webgl) return;
    runtime.handle = 0;
    // Time comes from the clock every frame, never from an accumulated delta.
    const elapsed = now - runtime.startedAt;
    const gate = gateRef.current;

    if (elapsed >= STING_HOLD_END_MS && !gate.released) {
      // Parked at the hold: nothing new is drawn and no frame is requested.
      gate.whenReleased(() => {
        if (runtime.disposed || !runtime.playing) return;
        // Rewind the clock to the hold boundary so the park cost no sting time.
        runtime.startedAt = performance.now() - STING_HOLD_END_MS;
        runtime.handle = requestAnimationFrame(shaderFrame);
      });
      return;
    }

    if (elapsed >= STING_END_MS) {
      finish();
      return;
    }

    runtime.webgl.drawAt(elapsed);
    runtime.handle = requestAnimationFrame(shaderFrame);
  }

  function startSting(): void {
    const runtime = runtimeRef.current;
    if (runtime.playing || runtime.disposed) return;

    // The mark may still be loading. Remember the request and start on arrival.
    if (!runtime.markImage) {
      runtime.pending = true;
      return;
    }

    runtime.playing = true;

    if (runtime.webgl) {
      runtime.startedAt = performance.now();
      runtime.handle = requestAnimationFrame(shaderFrame);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      runtime.playing = false;
      return;
    }
    runtime.cancel2d = playCanvas2dSting(
      canvas,
      runtime.markImage,
      () => {
        runtime.playing = false;
        runtime.cancel2d = null;
        onDoneRef.current?.();
      },
      gateRef.current,
    );
  }

  useEffect(() => {
    const runtime = runtimeRef.current;
    runtime.disposed = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = 600;
    canvas.height = 600;

    let cancelled = false;

    // Only ever ask for a WebGL2 context right here, immediately before
    // compiling into it. Doing this earlier (before the mark has even loaded)
    // would leave a WebGL2 context sitting on the canvas even when the
    // compile that follows throws, and a canvas that already holds a WebGL2
    // context can never hand out a 2D one afterwards.
    function afterMarkReady(image: HTMLImageElement): void {
      if (cancelled) return;
      runtime.markImage = image;

      if (!effectiveForce2d) {
        const gl = tryWebgl2(canvas!);
        if (gl) {
          try {
            runtime.webgl = createWebglSting(gl, image, { breakShader });
          } catch (error) {
            // A shader that will not compile is not worth taking the page
            // down for. This canvas now holds a WebGL2 context, though, so
            // the 2D fallback cannot use it: force a fresh canvas instead and
            // let the effect that follows the remount set the fallback up.
            console.warn('sting shader unavailable, using the 2D fallback', error);
            runtime.webgl = null;
            setFallbackForced(true);
            return;
          }
        }
      }

      if (runtime.pending) {
        runtime.pending = false;
        startSting();
      }
    }

    if (runtime.markImage) {
      // Already loaded in an earlier pass (the remount after a broken
      // shader): no need to fetch it again.
      afterMarkReady(runtime.markImage);
    } else {
      loadMark()
        .then(afterMarkReady)
        .catch((error: unknown) => {
          console.warn('sting mark unavailable', error);
          runtime.pending = false;
          // With no mark there is nothing to show, so finish straight away.
          onDoneRef.current?.();
        });
    }

    return () => {
      cancelled = true;
      runtime.disposed = true;
      runtime.playing = false;
      if (runtime.handle) cancelAnimationFrame(runtime.handle);
      runtime.handle = 0;
      runtime.cancel2d?.();
      runtime.cancel2d = null;
    };
    // Reruns only when a broken shader forces the fallback, which is exactly
    // when the canvas is remounted (see the `key` below) and needs its setup
    // effect to run again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveForce2d]);

  return (
    <canvas
      key={fallbackForced ? 'sting-canvas-2d-fallback' : 'sting-canvas'}
      ref={canvasRef}
      className="glasses-sting"
      width={600}
      height={600}
    />
  );
}
